import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PuffLoader } from 'react-spinners';

import amplitude from '@/amplitude';
import { AuthGuard } from '@/features/auth/components';
import { BillingPeriodToggle } from '@/features/subscription/components/BillingPeriodToggle';
import { SubscriptionPlanCard } from '@/features/subscription/components/SubscriptionPlanCard';
import { SubscriptionPlanCardSkeleton } from '@/features/subscription/components/SubscriptionPlanCardSkeleton';
import { useSubscriptionData, useSubscriptionSubscribeRedirect } from '@/features/subscription/hooks';
import type { BillingPeriod } from '@/features/subscription/types';
import { RoundedCloseButton } from '@/shared/components/ui/RoundedCloseButton';

export function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Track subscription success with Amplitude if ?success=true in URL
  const isSubscriptionSuccess = searchParams.get('success') === 'true';
  useEffect(() => {
    if (isSubscriptionSuccess) {
      amplitude.track('Paiement Completed');
    }
  }, [isSubscriptionSuccess]);

  // Get billing_period from URL params, default to 'monthly' if not present or invalid
  const getInitialBillingPeriod = (): BillingPeriod => {
    const billingPeriodParam = searchParams.get('billing_period');
    return billingPeriodParam === 'yearly' ? 'yearly' : 'monthly';
  };
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(getInitialBillingPeriod);

  // Get subscription data
  const { subscriptionPlans, loading, currentSubscriptionPeriodicity } = useSubscriptionData(billingPeriod);

  // Update billing period when URL params change or when current subscription data is loaded
  useEffect(() => {
    const billingPeriodParam = searchParams.get('billing_period');

    if (billingPeriodParam) {
      // Si billing_period est défini dans l'URL, l'utiliser
      const newBillingPeriod: BillingPeriod = billingPeriodParam === 'yearly' ? 'yearly' : 'monthly';
      setBillingPeriod(newBillingPeriod);
    } else if (currentSubscriptionPeriodicity) {
      // Si pas de billing_period dans l'URL mais l'utilisateur a un abonnement, utiliser sa périodicité
      setBillingPeriod(currentSubscriptionPeriodicity);
    }
  }, [searchParams, currentSubscriptionPeriodicity]);

  // Handle subscription redirects based on URL params
  const { isLoading: isSubscriptionRedirectLoading } = useSubscriptionSubscribeRedirect({
    subscriptionPlans,
    subscriptionPlansLoading: loading,
  });

  if (isSubscriptionRedirectLoading) {
    return (
      <div
        className={center({
          w: '100vw',
          h: '100vh',
        })}
      >
        <PuffLoader color={token.var('colors.accent.primary')} size={60} />
      </div>
    );
  }

  const pageStyles = css({
    inset: 0,
    overflow: 'clip',
    pb: '2rem',
  });

  const backgroundGradientStyles = css({
    position: 'fixed',
    top: '-10vh',
    left: '-50vmin',
    w: '110vmin',
    h: '110vmin',
    background: 'radial-gradient(circle, rgba(94, 105, 211, 0.3) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  });

  const contentStyles = css({
    overflowY: 'auto',
  });

  const closeButtonStyles = css({
    position: 'fixed',
    top: '1.9375rem',
    right: '1.9375rem',
  });

  const titleStyles = css({
    fontSize: '2xl', // 25px du Figma
    fontWeight: 'bold',
    color: 'font.primary',
    textAlign: 'center',
    mt: '2rem',
    mb: '1.5rem',
    fontFamily: 'Manrope, sans-serif',
    lineHeight: '1.07',
    '& span:last-child': {
      color: 'accent.primary',
    },
  });

  const toggleContainerStyles = css({
    display: 'flex',
    justifyContent: 'center',
    mb: '2.5rem',
  });

  const cardsContainerStyles = css({
    display: 'flex',
    justifyContent: 'center',
    gap: '3.75rem', // 60px du Figma
    flexWrap: 'wrap',
    px: '2rem',
  });

  const getPlanPrice = (plan: NonNullable<typeof subscriptionPlans>['customPlans'][number]) => {
    if (billingPeriod === 'yearly' && plan.priceYearly) {
      return {
        monthlyPrice: Math.round((plan.priceYearly / 12) * 100) / 100,
        baseMonthlyPrice: plan.priceMonthly,
      };
    }
    return {
      monthlyPrice: plan.priceMonthly,
      baseMonthlyPrice: undefined,
    };
  };

  return (
    <AuthGuard>
      <div className={pageStyles}>
        {/* Background gradient comme dans le Figma */}
        <div className={backgroundGradientStyles} />

        <div className={contentStyles}>
          {/* Close button */}
          <RoundedCloseButton className={closeButtonStyles} onClick={() => navigate('/')} />

          {/* Title */}
          <h1 className={titleStyles}>
            <span>Customise ton abonnement </span>
            <span>Recop !</span>
          </h1>

          {/* Billing Period Toggle */}
          <div className={toggleContainerStyles}>
            <BillingPeriodToggle value={billingPeriod} onChange={setBillingPeriod} discount="-15%" disabled={loading} />
          </div>

          {/* Subscription Plans */}
          <div className={cardsContainerStyles}>
            {loading ? (
              <>
                <SubscriptionPlanCardSkeleton />
                <SubscriptionPlanCardSkeleton />
                <SubscriptionPlanCardSkeleton />
              </>
            ) : (
              <>
                {/* Beginner Plan */}
                <SubscriptionPlanCard
                  planName={subscriptionPlans?.beginner.name ?? '\u2212'}
                  {...(subscriptionPlans?.beginner ? getPlanPrice(subscriptionPlans?.beginner) : {})}
                  freeTrialDays={subscriptionPlans?.beginner.trialPeriodDays ?? 0}
                  isHighlighted={true}
                  subscriptionType="subscribe"
                  features={[
                    {
                      label: 'Analyses de produits simultanées',
                      value: subscriptionPlans?.beginner.catalogAnalysisQuotas ?? '\u2212',
                    },
                    {
                      label: 'Analyses de vendeurs simultanées',
                      value: subscriptionPlans?.beginner.profileAnalysisQuotas ?? '\u2212',
                    },
                    { label: 'Analyses publiques', value: 'Illimité' },
                  ]}
                  isCurrentPlan={subscriptionPlans?.beginner.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.beginner.isDowngrade}
                  handleSubscribe={subscriptionPlans?.beginner.handleSubscribe}
                  handleCancel={subscriptionPlans?.beginner.handleCancel}
                />

                {/* Classic Plan */}
                <SubscriptionPlanCard
                  planName={subscriptionPlans?.classic.name ?? '\u2212'}
                  {...(subscriptionPlans?.classic ? getPlanPrice(subscriptionPlans?.classic) : {})}
                  freeTrialDays={subscriptionPlans?.classic.trialPeriodDays ?? 0}
                  isHighlighted={true}
                  subscriptionType="subscribe"
                  features={[
                    {
                      label: 'Analyses de produits simultanées',
                      value: subscriptionPlans?.classic.catalogAnalysisQuotas ?? '\u2212',
                    },
                    {
                      label: 'Analyses de vendeurs simultanées',
                      value: subscriptionPlans?.classic.profileAnalysisQuotas ?? '\u2212',
                    },
                    { label: 'Analyses publiques', value: 'Illimité' },
                  ]}
                  isCurrentPlan={subscriptionPlans?.classic.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.classic.isDowngrade}
                  handleSubscribe={subscriptionPlans?.classic.handleSubscribe}
                  handleCancel={subscriptionPlans?.classic.handleCancel}
                />

                {/* Pro Plan */}
                <SubscriptionPlanCard
                  planName={subscriptionPlans?.pro.name ?? '\u2212'}
                  {...(subscriptionPlans?.pro ? getPlanPrice(subscriptionPlans?.pro) : {})}
                  freeTrialDays={subscriptionPlans?.pro.trialPeriodDays ?? 0}
                  isHighlighted={false}
                  subscriptionType="subscribe"
                  features={[
                    {
                      label: 'Analyses de produits simultanées',
                      value: subscriptionPlans?.pro.catalogAnalysisQuotas ?? '\u2212',
                    },
                    {
                      label: 'Analyses de vendeurs simultanées',
                      value: subscriptionPlans?.pro.profileAnalysisQuotas ?? '\u2212',
                    },
                    { label: 'Analyses publiques', value: 'Illimité' },
                  ]}
                  isCurrentPlan={subscriptionPlans?.pro.isCurrentPlan}
                  isDowngrade={subscriptionPlans?.pro.isDowngrade}
                  handleSubscribe={subscriptionPlans?.pro.handleSubscribe}
                  handleCancel={subscriptionPlans?.pro.handleCancel}
                />

                {/* Expert Plan - Contact */}
                <SubscriptionPlanCard
                  planName="Recop Expert"
                  subscriptionType="contact"
                  features={[
                    { label: 'Analyses de produits simultanées', value: 'Illimité' },
                    { label: 'Analyses de vendeurs simultanées', value: 'Illimité' },
                    { label: 'Analyses publiques', value: 'Illimité' },
                  ]}
                  handleContact={() => navigate('/contact')}
                />
              </>
            )}

            {!loading &&
              subscriptionPlans?.customPlans &&
              subscriptionPlans.customPlans
                .filter(
                  (plan) =>
                    (billingPeriod === 'monthly' && plan.priceMonthly) ||
                    (billingPeriod === 'yearly' && plan.priceYearly),
                )
                .map((customPlan) => (
                  <SubscriptionPlanCard
                    key={customPlan.id}
                    planName={customPlan.name}
                    {...getPlanPrice(customPlan)}
                    freeTrialDays={customPlan.trialPeriodDays}
                    isHighlighted={false}
                    subscriptionType="subscribe"
                    features={[
                      {
                        label: 'Analyses de produits simultanées',
                        value: customPlan.catalogAnalysisQuotas,
                      },
                      {
                        label: 'Analyses de vendeurs simultanées',
                        value: customPlan.profileAnalysisQuotas,
                      },
                      { label: 'Analyses publiques', value: 'Illimité' },
                    ]}
                    isCurrentPlan={customPlan.isCurrentPlan}
                    isDowngrade={customPlan.isDowngrade}
                    handleSubscribe={customPlan.handleSubscribe}
                    handleCancel={customPlan.handleCancel}
                  />
                ))}
          </div>
        </div>
      </div>
    </AuthGuard>
  );
}
